import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Calendar,
  Plus,
  Search,
  Download,
  Edit,
  Trash2,
  Eye,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  X,
  Building2,
  FileText,
  User,
  Upload,
  Paperclip,
  Wrench,
  PhoneCall
} from 'lucide-react';
import { visitService } from '../../../services/visitService';
import { contractService } from '../../../services/contractService';
import { serviceReportService } from '../../../services/serviceReportService';
import { useDateFormat } from '../../../hooks/useDateFormat';

const Visits = () => {
  const { t } = useTranslation();
  const [visits, setVisits] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('list');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Visit modal state
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [visitFormData, setVisitFormData] = useState({
    contractId: '',
    visitNumber: '',
    visitType: 'ROUTINE',
    scheduledDate: '',
    technicianName: '',
    technicianContact: '',
    purpose: '',
    notes: ''
  });

  // Service Report modal state
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [editingReport, setEditingReport] = useState(null);
  const [reportFormData, setReportFormData] = useState({
    reportNumber: '',
    reportDate: '',
    workPerformed: '',
    findings: '',
    recommendations: '',
    laborHours: '',
    laborCost: '',
    partsCost: '',
    totalCost: '',
    assetCondition: 'GOOD',
    nextServiceDate: '',
    signedBy: ''
  });
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [existingFiles, setExistingFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // View Report modal
  const [showViewReportModal, setShowViewReportModal] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);
  const { formatDate } = useDateFormat();

  // Mock data
  const mockVisits = [
    {
      id: 1,
      visitNumber: 'VIS-2024-001',
      visitType: 'ROUTINE',
      contractId: 1,
      contractNumber: 'MC-2024-001',
      contractTitle: 'Annual HVAC Maintenance',
      supplierId: 1,
      supplierName: 'TechMaint Solutions',
      scheduledDate: '2024-02-15',
      actualDate: '2024-02-15',
      status: 'COMPLETED',
      technicianName: 'Mike Johnson',
      technicianContact: '+1 555-1234',
      purpose: 'Quarterly inspection and filter replacement',
      hasReport: true,
      report: {
        id: 1,
        reportNumber: 'SR-2024-001',
        reportDate: '2024-02-15',
        workPerformed: 'Completed quarterly HVAC inspection. Replaced air filters on units 1-4.',
        findings: 'Unit 2 showing signs of wear on fan belt.',
        recommendations: 'Schedule fan belt replacement for Unit 2.',
        laborHours: 4.5,
        laborCost: 450,
        partsCost: 180,
        totalCost: 630,
        assetCondition: 'GOOD',
        nextServiceDate: '2024-05-15',
        signedBy: 'Mike Johnson',
        files: []
      }
    },
    {
      id: 2,
      visitNumber: 'VIS-2024-002',
      visitType: 'ROUTINE',
      contractId: 1,
      contractNumber: 'MC-2024-001',
      contractTitle: 'Annual HVAC Maintenance',
      supplierId: 1,
      supplierName: 'TechMaint Solutions',
      scheduledDate: '2024-05-15',
      actualDate: null,
      status: 'SCHEDULED',
      technicianName: 'Mike Johnson',
      technicianContact: '+1 555-1234',
      purpose: 'Q2 inspection',
      hasReport: false,
      report: null
    },
    {
      id: 3,
      visitNumber: 'VIS-2024-003',
      visitType: 'ROUTINE',
      contractId: 2,
      contractNumber: 'MC-2024-002',
      contractTitle: 'Generator Maintenance Contract',
      supplierId: 2,
      supplierName: 'Global Parts Inc',
      scheduledDate: '2024-03-01',
      actualDate: '2024-03-02',
      status: 'COMPLETED',
      technicianName: 'Sarah Williams',
      technicianContact: '+1 555-5678',
      purpose: 'Generator servicing and oil change',
      hasReport: true,
      report: {
        id: 2,
        reportNumber: 'SR-2024-002',
        reportDate: '2024-03-02',
        workPerformed: 'Performed complete generator service including oil change and filter replacement.',
        findings: 'All systems operating within normal parameters.',
        recommendations: 'Continue regular maintenance schedule.',
        laborHours: 3,
        laborCost: 300,
        partsCost: 250,
        totalCost: 550,
        assetCondition: 'GOOD',
        nextServiceDate: '2024-06-02',
        signedBy: 'Sarah Williams',
        files: []
      }
    },
    {
      id: 4,
      visitNumber: 'VIS-2024-004',
      visitType: 'ON_CALL',
      contractId: 1,
      contractNumber: 'MC-2024-001',
      contractTitle: 'Annual HVAC Maintenance',
      supplierId: 1,
      supplierName: 'TechMaint Solutions',
      scheduledDate: '2024-01-10',
      actualDate: null,
      status: 'MISSED',
      technicianName: 'Mike Johnson',
      technicianContact: '+1 555-1234',
      purpose: 'Emergency repair - Unit 3 not cooling',
      hasReport: false,
      report: null
    },
    {
      id: 5,
      visitNumber: 'VIS-2024-005',
      visitType: 'ON_CALL',
      contractId: 2,
      contractNumber: 'MC-2024-002',
      contractTitle: 'Generator Maintenance Contract',
      supplierId: 2,
      supplierName: 'Global Parts Inc',
      scheduledDate: '2024-04-20',
      actualDate: null,
      status: 'IN_PROGRESS',
      technicianName: 'Tom Brown',
      technicianContact: '+1 555-9012',
      purpose: 'Emergency repair - Starter motor failure',
      hasReport: false,
      report: null
    }
  ];

  const mockContracts = [
    { id: 1, contractNumber: 'MC-2024-001', title: 'Annual HVAC Maintenance' },
    { id: 2, contractNumber: 'MC-2024-002', title: 'Generator Maintenance Contract' }
  ];

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- helper identity changes each render; dependency list is intentional
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Try to load from API, fallback to mock data
      const [visitsData, contractsData] = await Promise.all([
        visitService.getVisits().catch((err) => {
          console.warn('Failed to load visits from API:', err);
          return null;
        }),
        contractService.getContracts().catch((err) => {
          console.warn('Failed to load contracts from API:', err);
          return null;
        })
      ]);

      // Transform backend response to frontend format
      const transformedVisits = visitsData ? visitsData.map(visit => ({
        ...visit,
        // Ensure visitType defaults to ROUTINE if not present
        visitType: visit.visitType || 'ROUTINE',
        // Map hasReport from backend
        hasReport: visit.hasReport || false,
        // Report will be loaded separately when needed
        report: null
      })) : mockVisits;

      setVisits(transformedVisits);
      setContracts(contractsData || mockContracts);
    } catch (error) {
      console.error('Error loading data:', error);
      setVisits(mockVisits);
      setContracts(mockContracts);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      SCHEDULED: { bg: 'bg-blue-100 text-blue-800', icon: Clock, label: t('cmms.visits_scheduled') },
      IN_PROGRESS: { bg: 'bg-yellow-100 text-yellow-800', icon: Clock, label: t('cmms.inProgress') },
      COMPLETED: { bg: 'bg-green-100 text-green-800', icon: CheckCircle, label: t('cmms.completed') },
      CANCELLED: { bg: 'bg-gray-100 text-gray-800', icon: XCircle, label: t('cmms.wo_cancelled') },
      MISSED: { bg: 'bg-red-100 text-red-800', icon: AlertTriangle, label: t('cmms.vis_statusMissed') }
    };
    return configs[status] || configs.SCHEDULED;
  };

  const getVisitTypeConfig = (type) => {
    return type === 'ON_CALL'
      ? { bg: 'bg-orange-100 text-orange-800', icon: PhoneCall, label: t('cmms.vis_typeOnCall') }
      : { bg: 'bg-purple-100 text-purple-800', icon: Wrench, label: t('cmms.vis_typeRoutine') };
  };

  const getConditionLabel = (condition) => {
    const map = {
      GOOD: t('cmms.sr_condGood'),
      FAIR: t('cmms.sr_condFair'),
      POOR: t('cmms.sr_condPoor'),
      CRITICAL: t('cmms.sr_condCritical')
    };
    return map[condition] || condition;
  };

  const getConditionBadge = (condition) => {
    const styles = {
      GOOD: 'bg-green-100 text-green-800',
      FAIR: 'bg-yellow-100 text-yellow-800',
      POOR: 'bg-orange-100 text-orange-800',
      CRITICAL: 'bg-red-100 text-red-800'
    };
    return styles[condition] || styles.GOOD;
  };

  const filteredVisits = visits.filter(visit => {
    const matchesStatus = filterStatus === 'all' || visit.status === filterStatus;
    const matchesType = filterType === 'all' || visit.visitType === filterType;
    const matchesSearch =
      visit.visitNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (visit.contractTitle ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (visit.supplierName ?? '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      visit.technicianName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesType && matchesSearch;
  });

  // Visit handlers
  const handleCreateVisit = () => {
    setEditingVisit(null);
    setVisitFormData({
      contractId: '',
      visitNumber: '',
      visitType: 'ROUTINE',
      scheduledDate: '',
      technicianName: '',
      technicianContact: '',
      purpose: '',
      notes: ''
    });
    setShowVisitModal(true);
  };

  const handleEditVisit = (visit) => {
    setEditingVisit(visit);
    setVisitFormData({
      contractId: visit.contractId,
      visitNumber: visit.visitNumber,
      visitType: visit.visitType || 'ROUTINE',
      scheduledDate: visit.scheduledDate,
      technicianName: visit.technicianName || '',
      technicianContact: visit.technicianContact || '',
      purpose: visit.purpose || '',
      notes: visit.notes || ''
    });
    setShowVisitModal(true);
  };

  const handleSubmitVisit = async (e) => {
    e.preventDefault();
    try {
      if (editingVisit) {
        await visitService.updateVisit(editingVisit.id, visitFormData);
      } else {
        await visitService.createVisit(visitFormData);
      }
      setShowVisitModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving visit:', error);
    }
  };

  const handleUpdateStatus = async (visitId, newStatus) => {
    try {
      await visitService.updateVisitStatus(visitId, newStatus);
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDeleteVisit = async (id) => {
    if (window.confirm('Are you sure you want to delete this visit?')) {
      try {
        await visitService.deleteVisit(id);
        loadData();
      } catch (error) {
        console.error('Error deleting visit:', error);
      }
    }
  };

  // Service Report handlers
  const handleAddReport = (visit) => {
    setSelectedVisit(visit);
    setEditingReport(null);
    setReportFormData({
      reportNumber: `SR-${new Date().getFullYear()}-${String(visits.length + 1).padStart(3, '0')}`,
      reportDate: new Date().toISOString().split('T')[0],
      workPerformed: '',
      findings: '',
      recommendations: '',
      laborHours: '',
      laborCost: '',
      partsCost: '',
      totalCost: '',
      assetCondition: 'GOOD',
      nextServiceDate: '',
      signedBy: visit.technicianName || ''
    });
    setFilesToUpload([]);
    setExistingFiles([]);
    setShowReportModal(true);
  };

  const handleEditReport = async (visit) => {
    try {
      let report = visit.report;

      // Fetch report from API if we have reportId but no embedded report
      if (!report && visit.reportId) {
        report = await serviceReportService.getReportById(visit.reportId);
      }

      if (!report) {
        alert('Report not found');
        return;
      }

      setSelectedVisit(visit);
      setEditingReport(report);
      setReportFormData({
        reportNumber: report.reportNumber,
        reportDate: report.reportDate,
        workPerformed: report.workPerformed || '',
        findings: report.findings || '',
        recommendations: report.recommendations || '',
        laborHours: report.laborHours || '',
        laborCost: report.laborCost || '',
        partsCost: report.partsCost || '',
        totalCost: report.totalCost || '',
        assetCondition: report.assetCondition || 'GOOD',
        nextServiceDate: report.nextServiceDate || '',
        signedBy: report.signedBy || ''
      });
      setFilesToUpload([]);
      setExistingFiles(report.files || []);
      setShowReportModal(true);
    } catch (error) {
      console.error('Error loading report for edit:', error);
      alert('Failed to load report. Please try again.');
    }
  };

  const handleViewReport = async (visit) => {
    try {
      // Fetch report from API if we have reportId
      if (visit.reportId) {
        const report = await serviceReportService.getReportById(visit.reportId);
        setViewingReport(report);
      } else if (visit.report) {
        // Fallback to embedded report (mock data)
        setViewingReport(visit.report);
      }
      setSelectedVisit(visit);
      setShowViewReportModal(true);
    } catch (error) {
      console.error('Error loading report:', error);
      alert('Failed to load report. Please try again.');
    }
  };

  const handleAddFile = (e) => {
    const files = Array.from(e.target.files);
    setFilesToUpload(prev => [...prev, ...files.map(file => ({ file, description: '' }))]);
    e.target.value = '';
  };

  const handleRemoveNewFile = (index) => {
    setFilesToUpload(prev => prev.filter((_, i) => i !== index));
  };

  const handleFileDescriptionChange = (index, description) => {
    setFilesToUpload(prev => prev.map((item, i) =>
      i === index ? { ...item, description } : item
    ));
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const reportData = {
        ...reportFormData,
        visitId: selectedVisit.id
      };

      let reportId;
      if (editingReport) {
        await serviceReportService.updateReport(editingReport.id, reportData);
        reportId = editingReport.id;
      } else {
        const newReport = await serviceReportService.createReport(reportData);
        reportId = newReport?.id;
      }

      // Upload any new files
      if (filesToUpload.length > 0 && reportId) {
        for (const fileItem of filesToUpload) {
          try {
            await serviceReportService.uploadFile(reportId, fileItem.file, fileItem.description);
          } catch (fileError) {
            console.error('Error uploading file:', fileItem.file.name, fileError);
          }
        }
      }

      setShowReportModal(false);
      loadData();
    } catch (error) {
      console.error('Error saving report:', error);
      alert('Failed to save report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Stats
  const stats = {
    total: visits.length,
    scheduled: visits.filter(v => v.status === 'SCHEDULED').length,
    completed: visits.filter(v => v.status === 'COMPLETED').length,
    pendingReports: visits.filter(v => v.status === 'COMPLETED' && !v.hasReport).length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('cmms.visits')}</h1>
          <p className="text-gray-600 mt-1">{t('cmms.visits_subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50">
            <Download className="w-5 h-5 mr-2" />
            {t('common.export')}
          </button>
          <button
            onClick={handleCreateVisit}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            {t('cmms.visits_schedule')} {t('cmms.visits')}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">{t('common.total')} {t('cmms.visits')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.scheduled}</div>
              <div className="text-sm text-gray-600">{t('cmms.visits_scheduled')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.completed}</div>
              <div className="text-sm text-gray-600">{t('cmms.completed')}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{stats.pendingReports}</div>
              <div className="text-sm text-gray-600">{t('cmms.visits_pendingReports')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('common.search') + '...'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('common.allTypes')}</option>
            <option value="ROUTINE">{t('cmms.vis_typeRoutine')}</option>
            <option value="ON_CALL">{t('cmms.vis_typeOnCall')}</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">{t('common.allStatus')}</option>
            <option value="SCHEDULED">{t('cmms.visits_scheduled')}</option>
            <option value="IN_PROGRESS">{t('cmms.inProgress')}</option>
            <option value="COMPLETED">{t('cmms.completed')}</option>
            <option value="MISSED">{t('cmms.vis_statusMissed')}</option>
            <option value="CANCELLED">{t('cmms.wo_cancelled')}</option>
          </select>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 ${viewMode === 'list' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-600'}`}
            >
              {t('cmms.wo_list')}
            </button>
            <button
              onClick={() => setViewMode('kanban')}
              className={`px-3 py-2 ${viewMode === 'kanban' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-600'}`}
            >
              {t('cmms.wo_kanban')}
            </button>
          </div>
        </div>
      </div>

      {/* List View */}
      {viewMode === 'list' ? (
        <div className="space-y-4">
          {loading && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">{t('common.loading')}...</p>
            </div>
          )}

          {!loading && filteredVisits.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
              <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('common.noResults')}</h3>
              <p className="text-gray-500 mb-4">{t('cmms.visits_emptyState')}</p>
              <button
                onClick={handleCreateVisit}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Plus className="w-5 h-5 mr-2" />
                {t('cmms.visits_schedule')} {t('cmms.visits')}
              </button>
            </div>
          )}

          {filteredVisits.map((visit) => {
            const statusConfig = getStatusConfig(visit.status);
            const typeConfig = getVisitTypeConfig(visit.visitType);
            const StatusIcon = statusConfig.icon;
            const TypeIcon = typeConfig.icon;

            return (
              <div key={visit.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-gray-900">{visit.visitNumber}</h3>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${typeConfig.bg}`}>
                          <TypeIcon className="w-3 h-3" />
                          {typeConfig.label}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full ${statusConfig.bg}`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </span>
                        {visit.hasReport && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            <FileText className="w-3 h-3" />
                            {t('cmms.vis_reportFiled')}
                          </span>
                        )}
                        {visit.status === 'COMPLETED' && !visit.hasReport && (
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
                            <AlertTriangle className="w-3 h-3" />
                            {t('cmms.vis_reportPending')}
                          </span>
                        )}
                      </div>
                      {visit.contractTitle && (
                        <p className="text-sm text-gray-500 mt-1">{visit.contractTitle}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                        {visit.supplierName && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4" />
                            {visit.supplierName}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(visit.scheduledDate)}
                        </span>
                        {visit.technicianName && (
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {visit.technicianName}
                          </span>
                        )}
                      </div>
                      {visit.purpose && (
                        <p className="text-sm text-gray-600 mt-2">{visit.purpose}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Status Actions */}
                    {visit.status === 'SCHEDULED' && (
                      <button
                        onClick={() => handleUpdateStatus(visit.id, 'IN_PROGRESS')}
                        className="px-3 py-1.5 bg-yellow-50 text-yellow-700 text-sm rounded-lg hover:bg-yellow-100"
                      >
                        {t('cmms.vis_start')}
                      </button>
                    )}
                    {visit.status === 'IN_PROGRESS' && (
                      <button
                        onClick={() => handleUpdateStatus(visit.id, 'COMPLETED')}
                        className="px-3 py-1.5 bg-green-50 text-green-700 text-sm rounded-lg hover:bg-green-100"
                      >
                        {t('cmms.vis_complete')}
                      </button>
                    )}

                    {/* Report Actions */}
                    {visit.status === 'COMPLETED' && !visit.hasReport && (
                      <button
                        onClick={() => handleAddReport(visit)}
                        className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('cmms.vis_addReport')}
                      </button>
                    )}
                    {visit.hasReport && (
                      <>
                        <button
                          onClick={() => handleViewReport(visit)}
                          className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          {t('cmms.vis_viewReport')}
                        </button>
                        <button
                          onClick={() => handleEditReport(visit)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                          title={t('common.edit')}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </>
                    )}

                    {/* Visit Actions */}
                    <button
                      onClick={() => handleEditVisit(visit)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Edit Visit"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteVisit(visit.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete Visit"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Kanban View */
        <div className="grid md:grid-cols-4 gap-6">
          {['SCHEDULED', 'IN_PROGRESS', 'COMPLETED', 'MISSED'].map((status) => {
            const config = getStatusConfig(status);
            const statusVisits = filteredVisits.filter(v => v.status === status);
            return (
              <div key={status} className="flex flex-col">
                <div className="flex items-center gap-2 mb-4">
                  <div className={`w-3 h-3 rounded-full ${config.bg.split(' ')[0]}`}></div>
                  <h3 className="font-semibold text-gray-900">{config.label}</h3>
                  <span className="ml-auto text-sm text-gray-500">{statusVisits.length}</span>
                </div>
                <div className="space-y-3 flex-1">
                  {statusVisits.map((visit) => {
                    const typeConfig = getVisitTypeConfig(visit.visitType);
                    return (
                      <div key={visit.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900">{visit.visitNumber}</span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded ${typeConfig.bg}`}>
                            {typeConfig.label}
                          </span>
                        </div>
                        {visit.contractTitle && (
                          <p className="text-sm text-gray-600 mb-2">{visit.contractTitle}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar className="w-3 h-3" />
                          {formatDate(visit.scheduledDate)}
                        </div>
                        {visit.supplierName && (
                          <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                            <Building2 className="w-3 h-3" />
                            {visit.supplierName}
                          </div>
                        )}
                        {visit.status === 'COMPLETED' && (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            {visit.hasReport ? (
                              <button
                                onClick={() => handleViewReport(visit)}
                                className="w-full text-xs text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1"
                              >
                                <FileText className="w-3 h-3" />
                                {t('cmms.vis_viewReport')}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleAddReport(visit)}
                                className="w-full text-xs text-orange-600 hover:text-orange-700 flex items-center justify-center gap-1"
                              >
                                <Plus className="w-3 h-3" />
                                {t('cmms.vis_addReport')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Visit Modal */}
      {showVisitModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingVisit ? `${t('common.edit')} ${t('cmms.visits')}` : `${t('cmms.visits_schedule')} ${t('cmms.visits')}`}
              </h2>
              <button onClick={() => setShowVisitModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmitVisit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.vis_contract')}</label>
                <select
                  value={visitFormData.contractId}
                  onChange={(e) => setVisitFormData({...visitFormData, contractId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('cmms.vis_noContract')}</option>
                  {contracts.map(c => (
                    <option key={c.id} value={c.id}>{c.contractNumber} - {c.title}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.vis_visitNumber')} *</label>
                  <input
                    type="text"
                    value={visitFormData.visitNumber}
                    onChange={(e) => setVisitFormData({...visitFormData, visitNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.vis_visitType')} *</label>
                  <select
                    value={visitFormData.visitType}
                    onChange={(e) => setVisitFormData({...visitFormData, visitType: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="ROUTINE">{t('cmms.vis_typeRoutineScheduled')}</option>
                    <option value="ON_CALL">{t('cmms.vis_typeOnCallEmergency')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.vis_scheduledDate')} *</label>
                <input
                  type="date"
                  value={visitFormData.scheduledDate}
                  onChange={(e) => setVisitFormData({...visitFormData, scheduledDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.vis_technicianName')}</label>
                  <input
                    type="text"
                    value={visitFormData.technicianName}
                    onChange={(e) => setVisitFormData({...visitFormData, technicianName: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.contact')}</label>
                  <input
                    type="text"
                    value={visitFormData.technicianContact}
                    onChange={(e) => setVisitFormData({...visitFormData, technicianContact: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.vis_purpose')}</label>
                <input
                  type="text"
                  value={visitFormData.purpose}
                  onChange={(e) => setVisitFormData({...visitFormData, purpose: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.notes')}</label>
                <textarea
                  value={visitFormData.notes}
                  onChange={(e) => setVisitFormData({...visitFormData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowVisitModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingVisit ? `${t('common.update')} ${t('cmms.visits')}` : `${t('cmms.visits_schedule')} ${t('cmms.visits')}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Service Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingReport ? `${t('common.edit')} ${t('cmms.sr_serviceReport')}` : `${t('common.create')} ${t('cmms.sr_serviceReport')}`}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {t('cmms.sr_visitLabel')} {selectedVisit?.visitNumber} - {selectedVisit?.contractTitle}
                </p>
              </div>
              <button onClick={() => setShowReportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmitReport} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_reportNumber')} *</label>
                  <input
                    type="text"
                    value={reportFormData.reportNumber}
                    onChange={(e) => setReportFormData({...reportFormData, reportNumber: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_reportDate')} *</label>
                  <input
                    type="date"
                    value={reportFormData.reportDate}
                    onChange={(e) => setReportFormData({...reportFormData, reportDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_workPerformed')} *</label>
                <textarea
                  value={reportFormData.workPerformed}
                  onChange={(e) => setReportFormData({...reportFormData, workPerformed: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_findings')}</label>
                <textarea
                  value={reportFormData.findings}
                  onChange={(e) => setReportFormData({...reportFormData, findings: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_recommendations')}</label>
                <textarea
                  value={reportFormData.recommendations}
                  onChange={(e) => setReportFormData({...reportFormData, recommendations: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_assetCondition')}</label>
                  <select
                    value={reportFormData.assetCondition}
                    onChange={(e) => setReportFormData({...reportFormData, assetCondition: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="GOOD">{t('cmms.sr_condGood')}</option>
                    <option value="FAIR">{t('cmms.sr_condFair')}</option>
                    <option value="POOR">{t('cmms.sr_condPoor')}</option>
                    <option value="CRITICAL">{t('cmms.sr_condCritical')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_nextServiceDate')}</label>
                  <input
                    type="date"
                    value={reportFormData.nextServiceDate}
                    onChange={(e) => setReportFormData({...reportFormData, nextServiceDate: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_laborHours')}</label>
                  <input
                    type="number"
                    step="0.5"
                    value={reportFormData.laborHours}
                    onChange={(e) => setReportFormData({...reportFormData, laborHours: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_laborCost')}</label>
                  <input
                    type="number"
                    value={reportFormData.laborCost}
                    onChange={(e) => setReportFormData({...reportFormData, laborCost: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_partsCost')}</label>
                  <input
                    type="number"
                    value={reportFormData.partsCost}
                    onChange={(e) => setReportFormData({...reportFormData, partsCost: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_totalCost')}</label>
                  <input
                    type="number"
                    value={reportFormData.totalCost}
                    onChange={(e) => setReportFormData({...reportFormData, totalCost: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('cmms.sr_signedBy')}</label>
                <input
                  type="text"
                  value={reportFormData.signedBy}
                  onChange={(e) => setReportFormData({...reportFormData, signedBy: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Document Attachments */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    {t('cmms.sr_reportAttachments')}
                  </h3>
                  <label className="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer text-sm">
                    <Upload className="w-4 h-4 mr-1" />
                    {t('cmms.con_addFile')}
                    <input
                      type="file"
                      onChange={handleAddFile}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                      multiple
                    />
                  </label>
                </div>

                {existingFiles.length > 0 && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-2">{t('cmms.sr_existingAttachments')}</p>
                    <div className="space-y-2">
                      {existingFiles.map((file) => (
                        <div key={file.id} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-green-600" />
                            <span className="text-sm text-gray-700">{file.fileName}</span>
                            <span className="text-xs text-gray-400">({formatFileSize(file.fileSize)})</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {filesToUpload.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-500 mb-2">{t('cmms.sr_filesToUpload')}</p>
                    <div className="space-y-2">
                      {filesToUpload.map((item, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg">
                          <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-700 truncate">{item.file.name}</p>
                            <input
                              type="text"
                              placeholder={t('cmms.sr_fileDescriptionPh')}
                              value={item.description}
                              onChange={(e) => handleFileDescriptionChange(index, e.target.value)}
                              className="w-full mt-1 px-2 py-1 text-xs border border-gray-200 rounded focus:ring-1 focus:ring-blue-500"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveNewFile(index)}
                            className="p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {existingFiles.length === 0 && filesToUpload.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4">
                    {t('cmms.sr_noAttachments')}
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowReportModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  disabled={submitting}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? `${t('common.save')}...` : (editingReport ? `${t('common.update')} ${t('cmms.sr_serviceReport')}` : `${t('common.create')} ${t('cmms.sr_serviceReport')}`)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Report Modal */}
      {showViewReportModal && viewingReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">{t('cmms.sr_serviceReport')}</h2>
                <p className="text-sm text-gray-500 mt-1">{viewingReport.reportNumber}</p>
              </div>
              <button onClick={() => setShowViewReportModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Header Info */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{t('cmms.sr_visitLabel')} {selectedVisit?.visitNumber}</p>
                  <p className="text-sm text-gray-500">{t('cmms.sr_dateLabel')} {formatDate(viewingReport.reportDate)}</p>
                </div>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getConditionBadge(viewingReport.assetCondition)}`}>
                  {getConditionLabel(viewingReport.assetCondition)}
                </span>
              </div>

              {/* Work Performed */}
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">{t('cmms.sr_workPerformed')}</h4>
                <p className="text-gray-600 bg-gray-50 rounded-lg p-3">{viewingReport.workPerformed}</p>
              </div>

              {/* Findings & Recommendations */}
              <div className="grid md:grid-cols-2 gap-4">
                {viewingReport.findings && (
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-yellow-800 mb-2">{t('cmms.sr_findings')}</h4>
                    <p className="text-sm text-yellow-700">{viewingReport.findings}</p>
                  </div>
                )}
                {viewingReport.recommendations && (
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">{t('cmms.sr_recommendations')}</h4>
                    <p className="text-sm text-blue-700">{viewingReport.recommendations}</p>
                  </div>
                )}
              </div>

              {/* Costs */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">{t('cmms.sr_costSummary')}</h4>
                <div className="grid grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-xs text-gray-500">{t('cmms.sr_laborHours')}</p>
                    <p className="text-lg font-semibold text-gray-900">{viewingReport.laborHours || 0}h</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('cmms.sr_laborCost')}</p>
                    <p className="text-lg font-semibold text-gray-900">{formatCurrency(viewingReport.laborCost)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('cmms.sr_partsCost')}</p>
                    <p className="text-lg font-semibold text-gray-900">{formatCurrency(viewingReport.partsCost)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{t('cmms.sr_totalCost')}</p>
                    <p className="text-lg font-bold text-blue-600">{formatCurrency(viewingReport.totalCost)}</p>
                  </div>
                </div>
              </div>

              {/* Footer Info */}
              <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {t('cmms.sr_signedByLabel')} {viewingReport.signedBy}
                </div>
                {viewingReport.nextServiceDate && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {t('cmms.sr_nextServiceLabel')} {formatDate(viewingReport.nextServiceDate)}
                  </div>
                )}
              </div>

              {/* Files */}
              {viewingReport.files && viewingReport.files.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    {t('cmms.sr_attachments')} ({viewingReport.files.length})
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {viewingReport.files.map((file) => (
                      <div key={file.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{file.fileName}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowViewReportModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('common.close')}
                </button>
                <button
                  onClick={() => {
                    setShowViewReportModal(false);
                    handleEditReport(selectedVisit);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {t('common.edit')} {t('cmms.sr_serviceReport')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Visits;
